<script lang="ts">
import { defineComponent, ref, onMounted, nextTick, onBeforeUnmount } from 'vue'
import TimelineUserNameWrapper from '@/components/timeline/TimelineUserNameWrapper.vue'
import LikeButton from '@/components/timeline/LikeButton.vue'
import TimelineComment from '@/components/timeline/TimelineComment.vue'
import CommentSource from '@/components/timeline/CommentSource.vue'
import ProgressIndicator from '@/components/loading/ProgressIndicator.vue'
import { mypagePath } from '@/generated/railsRoutes'
import hashScroll from '@/models/hashScroll'
import { useTimelineComment } from '@/composables/useTimelineComment'
import { useUserStore } from '@/stores/user'
import { useTimelineStore } from '@/stores/timeline'
import { useCommentStore } from '@/stores/comment'
import { formatStrToBoolean } from '@/utilities/formatter'

export default defineComponent({
  name: 'TimelineGlobal',

  components: {
    TimelineUserNameWrapper,
    LikeButton,
    TimelineComment,
    CommentSource,
    ProgressIndicator,
  },

  setup() {
    // store
    const userStore = useUserStore()
    const timelineStore = useTimelineStore()
    const commentStore = useCommentStore()

    const {
      // methods
      commentURL,
      isReply,
    } = useTimelineComment()

    const isOpen = ref(false)

    const TIMELINE_OPEN_CLOSE_SETTING_KEY = `${userStore.context.id}_timeline-open-close-setting`

    onMounted(async () => {
      // タイムラインの fetch 処理（store）
      await timelineStore.fetchTimeline()

      // タイムラインの開閉状態の初期設定（localstrage に開閉状態が保存されていなければデフォルトでは「open」状態とする）
      const _isOpen = localStorage.getItem(TIMELINE_OPEN_CLOSE_SETTING_KEY)
      isOpen.value = _isOpen === null || _isOpen === undefined
        ? true
        : formatStrToBoolean(localStorage.getItem(TIMELINE_OPEN_CLOSE_SETTING_KEY))

      /**
       * タイムラインのデータを取得しレンダリングされたら、タイムライン内のアンカーをイベントリスナに登録する
       * 同一ページ内でのハッシュリンクをクリックした際に、ブラウザでのページ遷移ではなく、スクリプトでのスムーススクロールで遷移させる
       * @description /foo/bar というページを表示中に、タイムライン上の /foo/bar#comment_baz というリンクをクリックした際に、同一ページのため、Vue 側の onMounted が発火しないため、イベントリスナに同一 pathname の場合のみネイティブのアンカーイベントを殺し、Vue 側でスムーススクロールを発火させるイベントをリスナに登録しておく
       * @description TemplateDefault.vue でも同内容の登録を行っているが、TemplateDefault の onMounted のタイミングでは、タイムラインの非同期取得が完了していないため、タイムライン側の取得完了後に再度登録を行っている（ただし、タイムラインが非表示のページもあるため、タイムライン側の登録のみに一本化せず、引き続き Template 側での登録も行う）
       */
      nextTick(async () => {
        // ページ内のアンカー要素をすべて拾い、同一ページのハッシュリンクであれば、ブラウザの遷移イベントをキャンセルし、ライブラリでのスムーススクロールで遷移させる
        document.querySelectorAll('a[href*="#"]').forEach(a => {
          const anchor = a as HTMLAnchorElement
          if (anchor.pathname === location.pathname && anchor.search === location.search) {
            a.addEventListener('click', hashClickHandler)
          }
        })
      })
    })

    /**
     * イベントリスナの解除
     */
    onBeforeUnmount(() => {
      if (!hashClickHandler) return
      document.querySelectorAll('a[href*="#"]').forEach(a => {
        const anchor = a as HTMLAnchorElement
        if (anchor.pathname === location.pathname && anchor.search === location.search) {
          a.removeEventListener('click', hashClickHandler!)
        }
      })
    })

    const toggleTimeline = () => {
      isOpen.value = !isOpen.value
      localStorage.setItem(TIMELINE_OPEN_CLOSE_SETTING_KEY, isOpen.value.toString())
    }

    /**
     * 「いいね」押したときの処理
     * @param item 対象のタイムラインアイテム
     */
    const likeComment = async (item: TimelineItem) => {
      if (await commentStore.likeComment(item.comment.id)) {
        if (item.comment.liked.byMe) {
          item.comment.liked.count--
        } else {
          item.comment.liked.count++
        }
        item.comment.liked.byMe = !item.comment.liked.byMe

        /**
         * タイムライン側で「いいね」した際に、レポートページのコンテンツエリア側の同コメントにも反映させるため、store 側にも更新をかける
         */
        const comment = commentStore.getComment(item.comment.id)
        if (comment && comment.liked) {
          comment.liked.byMe = !comment.liked.byMe
          comment.liked.total = comment.liked.byMe
            ? comment.liked.total + 1 : comment.liked.total - 1
        }
      }
    }

    /**
     * 同一ページ内でのハッシュリンクをクリックした際に、ブラウザでのページ遷移ではなく、ライブラリでのスムーススクロールで遷移させる
     * @param e イベントオブジェクト
     */
    const hashClickHandler = (e: Event) => {
      // TemplateDefault と TimelineGlobal が同一リンクに個別にハンドラを登録するため、
      // 既に一方が処理（preventDefault）済みなら何もしない（スムーススクロールの二重発火・ちらつき防止）
      if (e.defaultPrevented) return

      const target = e.currentTarget as HTMLAnchorElement
      if (target.hash && target.pathname === location.pathname && target.search === location.search) {
        const id = target.hash.slice(1) // ハッシュの先頭の「#」を削除
        hashScroll(id)
        e.preventDefault() // ブラウザ標準のジャンプを止める

        /**
         * 同一ページ内の他ハッシュへのリンクをクリックした場合
         * （例：foo#bar というアドレスの画面をレンダリング時、foo#baz へのリンクをクリックしてハッシュ遷移した場合）
         * 直前で preventDefault しているため、アドレスが書き換わらない現象が発生してしまうので（ブラウザのアンカーイベントを preventDefault で殺しているため）、
         * 手動で history を書き換えてアドレスを更新する。（pushState だと遷移した分全て履歴扱いになり、ブラウザバックの際に同一画面で多重に履歴が蓄積されるため、replaceState で 1 画面 1 履歴とする）
         */
        history.replaceState(null, '', target.hash)
      }
    }

    return {
      // routes
      mypagePath,

      // store
      userStore,
      timelineStore,

      // data
      isOpen,

      // methods
      toggleTimeline,
      commentURL,
      likeComment,
      isReply,
    }
  }
})
</script>

<template>
  <transition name="is-slide__in">
    <aside
      v-show="timelineStore.context.items.length"
      :class="[
        'f-aside__timeline',
        isOpen ? 'is-open' : 'is-close',
      ]">
      <div class="f-aside__timeline--body">
        <div class="l-unit__timeline">
          <div class="l-unit__timeline--header">
            <div class="o-heading__timeline">
              <h2
                class="o-heading__timeline--item u-action__tooltip"
                data-tooltip="マイページへ移動">
                <a
                  class="o-heading__timeline--link"
                  :href="mypagePath()">
                  <span class="o-heading__timeline--label is-primary">
                    マイ・トピックス
                  </span>
                  <span class="o-heading__timeline--label is-secondary">
                    <span class="span u-text__bold">
                      {{ userStore.context.displayName }}
                    </span>
                    さんの
                  </span>
                </a>
              </h2>
            </div>
          <!-- /.l-unit__timeline header --></div>

          <div class="l-unit__timeline--description">
            <div
              v-if="timelineStore.context.items.length"
              class="o-list__timeline">
              <ul class="o-list__timeline--items">
                <li
                  v-for="item in timelineStore.context.items"
                  :key="item.comment.id"
                  class="o-list__timeline--item">
                  <div class="l-unit__comment">
                    <!-- コメントユーザー名 -->
                    <div class="l-unit__comment--header">
                      <TimelineUserNameWrapper
                        :name="item.comment.user.name"
                        :avatar-url="item.comment.user.avatarUrl"
                        :company-name="item.comment.user.companyName"
                        :department-and-position="item.comment.user.positionName"
                        :comment="item.comment.user.message"
                        :introduction="item.comment.user.introduction"
                        :date="item.comment.date"
                        size="small" />
                    <!-- /.l-unit__comment header --></div>

                    <!-- コメント本文 -->
                    <div class="l-unit__comment--description">
                      <TimelineComment
                        :href="commentURL(item)"
                        :is-reply="isReply(item.comment.type)"
                        :max-line="3"
                        font-size="small">
                        {{ item.comment.body }}
                      </TimelineComment>
                    <!-- /.l-unit__comment description --></div>

                    <!-- いいね -->
                    <div
                      v-if="userStore.context.hasProfileForComment"
                      class="l-unit__comment--action is-like">
                      <LikeButton
                        :is-liked-by-me="item.comment.liked.byMe"
                        :total="item.comment.liked.count"
                        @click:like="likeComment(item)" />
                    <!-- /.l-unit__comment action --></div>

                    <!-- コメント元記事 -->
                    <div class="l-unit__comment--footer is-commentSource">
                      <CommentSource
                        :content="item.content"
                        :heading-options="{
                          ...{
                            fontSize: {
                              pc: '0.75em',
                              sp: '0.75em',
                            },
                            fontWeight: 'bold',
                            maxLine: 2,
                          }
                        }" />
                    <!-- /.l-unit__comment footer --></div>
                  <!-- /.l-unit__comment --></div>
                </li>
              </ul>
            <!-- /.o-list__timeline --></div>

            <div
              v-else
              class="o-label__empty">
              <p class="o-label__empty--item">
                最近のアクティビティはありません
              </p>
            <!-- /.o-label__empty --></div>
          <!-- /.l-unit__timeline description --></div>

          <div
            v-show="timelineStore.context.items.length"
            :class="[
              'l-unit__timeline--action',
              'is-toggle',
              isOpen ? 'is-open' : 'is-close',
            ]">
            <!--
              ボタンの場合は「展開」状態のときは「閉じる」ボタンになるので、
              ラッパー要素と状態修飾子のクラス名が逆転する
            -->
            <div
              :class="[
                'o-action__toggle',
                isOpen ? 'is-close' : 'is-open',
              ]">
              <button
                class="o-action__toggle--item"
                @click="toggleTimeline">
                {{ isOpen ? '閉じる' : '開く' }}
              </button>
            <!-- /.o-action__toggle --></div>
          <!-- /.l-unit__timeline action --></div>
        <!-- /.l-unit__timeline --></div>
      <!-- /.f-aside__timeline body --></div>

      <div
        class="f-aside__timeline--body is-loader"
        :data-screenlock="timelineStore.context.isFetching ? true : false">
        <ProgressIndicator />
      <!-- /.f-aside__timeline body --></div>
    <!-- /.f-aside__timeline --></aside>
  </transition>
</template>

<style lang="sass" scoped>
$timeline-prop: 16px

// vue transition
.is-slide__in-enter-active,
.is-slide__in-leave-active
  +transition($duration: $transition-long)

// foundation
.f-aside__timeline
  width: #{$timeline-width}px
  height: 100%
  position: fixed
  top: 0
  left: -#{$timeline-width}px
  background-color: rgba($bg-xx-light-gray, 1)
  box-shadow: 6px 0px 10px -10px rgba($bg-black, 0.6)
  z-index: 10
  +transition

  &.is-open
    left: 0

  & &--body
    height: 100%

    &.is-loader
      width: 100%
      display: flex
      flex-direction: column
      justify-content: center
      position: absolute
      top: 0
      left: 0
      visibility: hidden
      backdrop-filter: blur(6px)
      background-color: rgba($bg-white, 0.3)
      opacity: 0
      z-index: 1
      +transition

      &[data-screenlock="true"]
        visibility: visible
        opacity: 1

  +breakpoint($max-media: tablet)
    display: none

// layout
.l-unit__timeline
  height: 100%
  position: relative
  display: flex
  flex-direction: column

  & &--description
    flex-grow: 1
    overflow-y: auto
    border-top: 1px solid $border-x-light-gray

  & &--action
    &.is-toggle
      // position: absolute にして、right から位置計算した場合、
      // Safari, Firefox で位置計算ズレが発生するため、
      // fixed で left から計算する
      position: fixed
      top: 0
      left: 0
      font-size: $font-x-small
      +transition

      &.is-open
        left: #{$timeline-width}px

.l-unit__comment
  $root: &
  $avatar-size-px: 32
  $avatar-margin-px: 8
  display: grid
  grid-template-rows: auto auto auto
  grid-template-columns: 1fr auto
  position: relative

  & &--header
    grid-row: 1 / 2
    grid-column: 1 / 2
    cursor: default

  & &--description
    grid-row: 2 / 3
    grid-column: 1 / 3
    margin-top: 0.5em

  & &--action
    &.is-like
      grid-row: 1 / 2
      grid-column: 2 / 3

  & &--footer
    &.is-commentSource
      grid-row: 3 / 4
      grid-column: 1 / 3
      margin-top: 0.5em

// object
.o-heading__timeline
  & &--item
    line-height: 1.6
    font-weight: normal
    background-color: rgba($bg-white, 1)

  & &--link
    width: 100%
    height: 100%
    display: flex
    flex-direction: column
    position: relative
    color: $font-black
    text-decoration: none
    padding:
      top: 16px
      right: 30px
      bottom: 16px
      left: 16px
    +transition

    &::before,
    &::after
      content: ""
      width: 4px
      height: 4px
      display: block
      position: absolute
      top: 50%
      right: 16px
      transform: translateY(-50%) rotate(-45deg)
      margin-right: 1px
      border:
        bottom: 0.1em solid $border-x-dark-gray
        right: 0.1em solid $border-x-dark-gray

    &::after
      display: none

    +mouseover
      background-color: rgba($bg-orange, 0.2)

      &::before,
      &::after
        animation:
          name: arrowMoveTo
          duration: 1.5s
          fill-mode: backwards
          iteration-count: infinite
          timing-function: ease

      &::after
        display: block
        animation-delay: 0.75s

  & &--label
    &.is-primary
      font-weight: bold

    &.is-secondary
      order: -1
      font-size: $font-small

  @keyframes arrowMoveTo
    0%
      right: 16px
      opacity: 0

    50%
      opacity: 1

    100%
      right: 8px
      opacity: 0

.o-list__timeline
  & &--items
    padding: 0 $timeline-prop

  & &--item
    padding: 12px 0
    border-top: 1px solid $border-light-gray

    &:first-child
      border-top: none

.o-action__toggle
  $root: &
  $toggle-button-width: 2em

  & &--item
    height: 8.7em
    display: block
    position: relative
    line-height: $toggle-button-width
    color: $font-white
    -ms-writing-mode: tb-rl
    writing-mode: vertical-rl
    letter-spacing: 0.16em
    background-color: rgba($bg-black, 0.56)
    border-bottom-right-radius: $border-radius
    +transition

    $icon-size-px: 3.5
    $font-px: 12
    $icon-size-em: math.div($icon-size-px, $font-px)
    &::before
      content: ""
      width: #{$icon-size-em}em
      height: #{$icon-size-em}em
      display: inline-block
      transform: rotate(-135deg)
      margin:
        left: -0.1em
        bottom: 0.7em
      border:
        bottom: 0.15em solid $border-white
        left: 0.15em solid $border-white
      +transition($duration: $transition-long)

    +mouseover
      background-color: rgba($bg-black, 0.7)

  &.is-close
    #{$root}--item
      &::before
        margin-left: 0.3em
        transform: rotate(45deg)

.o-label__empty
  min-height: 10em
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center

  & &--item
    color: $font-gray
    font-size: $font-small
</style>
